import { EmblaCarouselType } from 'embla-carousel';
import { useCallback, useEffect, useState } from 'react';

import ArrowIcon from '@/fo/icons/base/icons/arrow.svg';
import ArrowThinIcon from '@/fo/icons/base/icons/arrow_thin.svg';
import ChevronIcon from '@/fo/icons/base/icons/chevron.svg';
import { UiKitButtonBase } from '@/fo/ui/kit/button/base';

import { styles } from './Arrows.styles';

export const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
  onButtonClick?: (emblaApi: EmblaCarouselType) => void
) => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    emblaApi.scrollPrev();
    onButtonClick?.(emblaApi);
  }, [emblaApi, onButtonClick]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    emblaApi.scrollNext();

    onButtonClick?.(emblaApi);
  }, [emblaApi, onButtonClick]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect).on('select', onSelect);

    return () => {
      emblaApi.off('reInit', onSelect).off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  return {
    onPrevButtonClick,
    onNextButtonClick,
    prevBtnDisabled,
    nextBtnDisabled
  };
};

export interface ArrowsProps {
  className?: string;
  arrowType?: 'arrow' | 'chevron' | 'chevronBold';
  classNameIcon?: string;
  onClick?: () => void;
}

export function PrevButton({
  className,
  arrowType = 'chevron',
  classNameIcon,
  ...props
}: ArrowsProps) {
  const slots = styles({});

  return (
    <UiKitButtonBase
      {...props}
      childrenClassName="flex items-center justify-center"
      className={slots.arrow({
        className: [
          'text-text-main-inversion -left-6 hover:bg-transparent hover:opacity-50',
          className
        ]
      })}
    >
      {arrowType === 'arrow' && <ArrowIcon className={classNameIcon} />}
      {arrowType === 'chevron' && <ArrowThinIcon className={classNameIcon} />}
      {arrowType === 'chevronBold' && <ChevronIcon className={classNameIcon} />}
    </UiKitButtonBase>
  );
}

export function NextButton({
  className,
  arrowType = 'chevron',
  classNameIcon,
  ...props
}: ArrowsProps) {
  const slots = styles({});

  return (
    <UiKitButtonBase
      {...props}
      childrenClassName="flex items-center justify-center"
      className={slots.arrow({
        className: [
          '-right-6 rotate-180 hover:bg-transparent hover:opacity-50',
          className
        ]
      })}
    >
      {arrowType === 'arrow' && <ArrowIcon className={classNameIcon} />}
      {arrowType === 'chevron' && <ArrowThinIcon className={classNameIcon} />}
      {arrowType === 'chevronBold' && <ChevronIcon className={classNameIcon} />}
    </UiKitButtonBase>
  );
}
